// src/pages/ContributeGroup.js
import React, { useMemo, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
    Box,
    Container,
    Card,
    CardContent,
    Typography,
    Button,
    Stack,
    Divider,
    TextField,
    InputAdornment,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
} from '@mui/material'
import {
    CheckCircle as CheckCircleIcon,
    ArrowBack as ArrowBackIcon,
    VolunteerActivism as DonateIcon,
} from '@mui/icons-material'

import { getGroupById, upsertGroup } from '../utils/groupsStorage'
import { useTransactions } from '../contexts/TransactionsContext'

const money = (amount, currency) => {
    try {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount)
    } catch {
        return `${currency} ${Number(amount || 0).toFixed(2)}`
    }
}

const makeId = () => {
    try {
        // eslint-disable-next-line no-undef
        return crypto.randomUUID()
    } catch {
        return `pay_${Date.now()}_${Math.random().toString(16).slice(2)}`
    }
}

const ContributeGroup = () => {
    const { groupId } = useParams()
    const navigate = useNavigate()
    const { addTransaction } = useTransactions()

    const [amount, setAmount] = useState('25')
    const [name, setName] = useState('')
    const [note, setNote] = useState('')
    const [submitting, setSubmitting] = useState(false)

    const [receiptOpen, setReceiptOpen] = useState(false)
    const [receipt, setReceipt] = useState(null)

    const group = useMemo(() => getGroupById(groupId), [groupId])

    const progress = useMemo(() => {
        if (!group) return { raised: 0, target: 0, pct: 0 }
        const raised = Number(group.raised || 0)
        const target = Number(group.target || 0)
        const pct = target > 0 ? Math.min(100, Math.round((raised / target) * 100)) : 0
        return { raised, target, pct }
    }, [group])

    const submitPayment = async () => {
        if (!group) return
        const amt = Number(amount || 0)
        if (!amt || amt <= 0) return

        setSubmitting(true)
        try {
            const paymentId = makeId()
            const createdAt = new Date().toISOString()

            const payment = {
                id: paymentId,
                createdAt,
                name: name?.trim() || 'Anonymous',
                note: note?.trim() || '',
                amount: amt,
                currency: group.currency,
                status: 'completed',
            }

            const nextGroup = {
                ...group,
                raised: Number(group.raised || 0) + amt,
                payments: Array.isArray(group.payments) ? [payment, ...group.payments] : [payment],
            }

            upsertGroup(nextGroup)

            // Save a receipt row in Transactions too (so the owner can see it later)
            const savedTx = addTransaction({
                type: 'contribution_payment',
                status: 'completed',
                currency: group.currency,
                amount: amt,
                title: `${payment.name} contributed to ${group.name}`,
                meta: {
                    receiptType: 'contribution_payment',
                    groupId: group.id,
                    groupName: group.name,
                    templateName: group.templateName,
                    contributorName: payment.name,
                    note: payment.note,
                    paymentId,
                },
            })

            setReceipt({
                receiptId: savedTx.id,
                paymentId,
                groupId: group.id,
                groupName: group.name,
                templateName: group.templateName,
                currency: group.currency,
                amount: amt,
                contributor: payment.name,
                dateText: new Date(savedTx.createdAt).toLocaleString(),
            })

            setReceiptOpen(true)
            setAmount('25')
            setName('')
            setNote('')
        } finally {
            setSubmitting(false)
        }
    }

    // If group doesn't exist, show the same “Link not found” style
    if (!group) {
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 6 }}>
                <Container maxWidth="lg">
                    <Card sx={{ borderRadius: 6 }}>
                        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                            <Typography variant="h5" sx={{ fontWeight: 900, mb: 1 }}>
                                Link not found
                            </Typography>
                            <Typography sx={{ color: 'text.secondary', mb: 3 }}>
                                This group link is missing. Ask the organizer to create the group again and share the new link.
                            </Typography>

                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                <Button variant="contained" onClick={() => navigate('/contributions')}>
                                    Go to Contributions
                                </Button>
                                <Button variant="outlined" onClick={() => navigate('/')}>
                                    Go Home
                                </Button>
                            </Stack>
                        </CardContent>
                    </Card>
                </Container>
            </Box>
        )
    }

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: { xs: 5, md: 7 } }}>
            <Container maxWidth="lg">
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                    <Box sx={{ flex: 1 }}>
                        <Card sx={{ borderRadius: 6 }}>
                            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                                <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 1 }}>
                                    <DonateIcon sx={{ color: '#22c55e' }} />
                                    <Typography variant="h5" sx={{ fontWeight: 950 }}>
                                        Contribute to group
                                    </Typography>
                                </Stack>

                                <Typography sx={{ color: 'text.secondary', mb: 2 }}>
                                    You're contributing to:
                                </Typography>

                                <Typography sx={{ fontWeight: 950, fontSize: 18, mb: 0.5 }}>
                                    {group.name}
                                </Typography>
                                <Typography sx={{ color: 'text.secondary', mb: 2 }}>
                                    Template: {group.templateName}
                                </Typography>

                                <Divider sx={{ my: 2 }} />

                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Your name (optional)"
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label={`Amount (${group.currency})`}
                                            type="number"
                                            value={amount}
                                            onChange={(e) => setAmount(e.target.value)}
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start">{group.currency}</InputAdornment>,
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Note (optional)"
                                            value={note}
                                            onChange={(e) => setNote(e.target.value)}
                                        />
                                    </Grid>
                                </Grid>

                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 3 }}>
                                    <Button
                                        variant="outlined"
                                        startIcon={<ArrowBackIcon />}
                                        onClick={() => navigate('/')}
                                    >
                                        Back home
                                    </Button>

                                    <Button
                                        variant="contained"
                                        onClick={submitPayment}
                                        disabled={submitting}
                                        sx={{ fontWeight: 950 }}
                                    >
                                        {submitting ? 'Processing...' : 'Contribute now'}
                                    </Button>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Box>

                    <Box sx={{ width: { xs: '100%', md: 420 } }}>
                        <Card sx={{ borderRadius: 6 }}>
                            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                                <Typography sx={{ fontWeight: 950, mb: 1 }}>Progress</Typography>

                                <Box sx={{ display: 'grid', gap: 1.1 }}>
                                    <Row label="Raised" value={money(progress.raised, group.currency)} />
                                    <Row label="Target" value={money(progress.target, group.currency)} />
                                    <Row label="Progress" value={`${progress.pct}%`} />
                                    <Row label="Members" value={String(group.members || 0)} />
                                </Box>

                                <Divider sx={{ my: 2 }} />

                                <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                                    This page is for family and friends.
                                    The organizer creates the group once and shares this link.
                                </Typography>
                            </CardContent>
                        </Card>
                    </Box>
                </Stack>

                {/* Receipt modal */}
                <Dialog open={receiptOpen} onClose={() => setReceiptOpen(false)} maxWidth="sm" fullWidth>
                    <DialogTitle sx={receiptTitleStyle}>
                        <Stack direction="row" spacing={1.2} alignItems="center">
                            <CheckCircleIcon sx={{ color: '#22c55e' }} />
                            <Typography sx={{ fontWeight: 950 }}>Contribution receipt</Typography>
                        </Stack>
                    </DialogTitle>

                    <DialogContent sx={receiptContentStyle}>
                        <Box sx={receiptCardStyle}>
                            <Typography sx={{ color: 'white', fontWeight: 950, fontSize: 18, mb: 1.5 }}>
                                {receipt?.groupName}
                            </Typography>

                            <Typography sx={{ color: 'rgba(255,255,255,0.70)', fontWeight: 850, mb: 1.2 }}>
                                Template: {receipt?.templateName}
                            </Typography>

                            <Divider sx={{ borderColor: 'rgba(255,255,255,0.10)', mb: 2 }} />

                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <Label>Receipt ID</Label>
                                    <Value>{receipt?.receiptId}</Value>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Label>Date</Label>
                                    <Value>{receipt?.dateText}</Value>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Label>Contributor</Label>
                                    <Value>{receipt?.contributor}</Value>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Label>Amount</Label>
                                    <Value>{money(receipt?.amount || 0, receipt?.currency || group.currency)}</Value>
                                </Grid>
                            </Grid>
                        </Box>
                    </DialogContent>

                    <DialogActions sx={receiptActionsStyle}>
                        <Button onClick={() => setReceiptOpen(false)} sx={{ color: '#22c55e', fontWeight: 900 }}>
                            Close
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => navigate('/transactions')}
                            sx={{ borderRadius: 999, fontWeight: 950, bgcolor: '#22c55e', '&:hover': { bgcolor: '#16a34a' } }}
                        >
                            View in Transactions
                        </Button>
                    </DialogActions>
                </Dialog>
            </Container>
        </Box>
    )
}

const Row = ({ label, value }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
        <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontWeight: 800 }}>{label}</Typography>
        <Typography sx={{ color: 'white', fontWeight: 900 }}>{value}</Typography>
    </Box>
)

const Label = ({ children }) => (
    <Typography sx={{ color: 'rgba(255,255,255,0.60)', fontSize: 12, fontWeight: 900 }}>
        {children}
    </Typography>
)

const Value = ({ children }) => (
    <Typography sx={{ color: 'white', fontWeight: 900, wordBreak: 'break-word' }}>
        {children}
    </Typography>
)

const receiptTitleStyle = {
    bgcolor: 'rgba(15,23,42,0.98)',
    color: 'white',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
}

const receiptContentStyle = { bgcolor: 'rgba(15,23,42,0.98)', pt: 2.5, pb: 2.5 }

const receiptActionsStyle = {
    bgcolor: 'rgba(15,23,42,0.98)',
    borderTop: '1px solid rgba(255,255,255,0.08)',
    px: 3,
    py: 2.2,
}

const receiptCardStyle = {
    borderRadius: 6,
    px: 3,
    py: 2.8,
    bgcolor: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.08)',
    boxShadow: '0 30px 90px rgba(0,0,0,0.45)',
}

export default ContributeGroup
